<template>
  <div class="flex items-center gap-4">
    <BittsSelect
      ref="selectRef"
      :model-value="selectedAccountId"
      :options="accountOptions"
      :loading="isLoading"
      :searchable="true"
      placeholder="Search for an account"
      size="medium"
      :dynamic-options="true"
      :use-disabled-options="true"
      :dropdown-match-select-width="false"
      class="c-partner-mapping-tab__account-select w-[220px]"
      @search-changed="handleSearch"
      @change="handleSelection"
    >
      <template #option="{ option }">
        <div>
          <div class="text-neutral-text overflow-hidden text-ellipsis">
            {{ option.record_name }}
          </div>
          <div
            v-if="option.record_id !== '__loading__'"
            class="text-sm text-neutral-text-weak c-partner-mapping-tab__account-id"
          >
            ID: {{ option.record_id }}
          </div>
        </div>
      </template>
      <template #notFoundContent>
        <div class="text-sm p-12">
          <p class="text-neutral-text text-center font-bold mb-4">
            No matching {{ objectNamePlural }}
          </p>
          <p class="text-neutral-text-weak text-center">
            Make sure the partner account is created <br />in {{ sourceName }}.
          </p>
        </div>
      </template>
    </BittsSelect>
    <BittsTooltip
      v-if="selectedAccountId"
      placement="top"
      :mount-to-body="true"
    >
      <BittsButton
        variant="ghost"
        type="neutral"
        size="x-small"
        :center-icon="['fak', 'x']"
        aria-label="Leave unmapped"
        @click="handleClear"
      />
      <template #title>
        <p>Leave unmapped</p>
      </template>
    </BittsTooltip>
  </div>
</template>

<script setup lang="ts">
import { BittsButton, BittsSelect, BittsTooltip } from '@crossbeam/bitts';

import { ICellRendererParams } from '@ag-grid-community/core';
import { computed, nextTick, onMounted, ref, watch } from 'vue';

import { crossbeamApi } from '@/api';
import { captureException } from '@/errors';
import { useFlashesStore, useSourcesStore } from '@/stores';
import { Account } from '@/types/sales';

const props = defineProps<{
  params: ICellRendererParams & {
    feedId?: number;
    onMappingChanged?: (
      partnerId: string,
      masterId: string | null,
      masterName: string | null,
    ) => void;
    usedAccountIds?: Set<string>;
    sourceName?: string;
    objectNamePlural?: string;
  };
}>();

const sourcesStore = useSourcesStore();
const flashesStore = useFlashesStore();

const accounts = ref<Account[]>([]);
type AccountOption = {
  value: string;
  label: string;
  record_name: string;
  record_id: string;
  disabled?: boolean;
};

const accountOptions = ref<AccountOption[]>([]);
const isLoading = ref(false);
const searchTerm = ref('');
const selectRef = ref<InstanceType<typeof BittsSelect> | null>(null);
const hasTriggeredInitialSearch = ref(false);

const partnerName = computed(() => {
  return props.params.data?.partner_name ?? '';
});

const selectedAccountId = computed(() => {
  return props.params.data?.master_id || null;
});

const selectedAccountName = computed(() => {
  return props.params.data?.master_name || null;
});

const feedId = computed(() => props.params.feedId);

const sourceName = computed(() => props.params.sourceName ?? 'CRM');
const objectNamePlural = computed(
  () => props.params.objectNamePlural ?? 'accounts',
);

const validSourceIds = computed(() => {
  if (!feedId.value) return new Set<number>();
  const feedSources = sourcesStore.getSourcesByFeedId(feedId.value);
  return new Set(
    feedSources
      .filter((source) => source.mdm_type === 'account')
      .map((source) => source.id),
  );
});

function filterAccountsByFeed(accountsToFilter: Account[]) {
  return accountsToFilter.filter((account) =>
    validSourceIds.value.has(account.source_id),
  );
}

async function searchAccounts(query: string) {
  // Require at least 3 characters for search
  if (!query || query.length < 3) {
    // Preserve default option if it exists when clearing search results
    ensureDefaultOption();
    return;
  }

  if (!feedId.value) {
    flashesStore.addErrorFlash({
      message: 'Feed ID is missing. Cannot search accounts.',
    });
    return;
  }

  // Update searchTerm to track the current query (important when called directly from focus listener)
  searchTerm.value = query;

  // Preserve existing options while loading to prevent "No results found" message
  // If options are empty, add a temporary loading option to prevent "No results found"
  const hadOptions = accountOptions.value.length > 0;
  if (!hadOptions) {
    accountOptions.value = [
      {
        value: '__loading__',
        label: '',
        record_name: 'Loading...',
        record_id: '__loading__',
        disabled: true,
      },
    ];
  }
  isLoading.value = true;

  try {
    const queryParams: { term?: string } = {};
    if (query && query.length >= 3) {
      queryParams.term = query;
    }

    const { data, error } = await crossbeamApi.GET(
      '/v1/records/accounts/search',
      {
        params: {
          query: queryParams,
        },
      },
    );

    if (error) {
      captureException(error);
      throw error;
    }

    // Ignore stale results if user typed more characters while request was in flight
    if (query !== searchTerm.value) {
      return;
    }

    const allAccounts = data?.data || [];

    // Filter accounts by feed - if no valid source IDs, show all accounts as fallback
    let filteredAccounts =
      validSourceIds.value.size > 0
        ? filterAccountsByFeed(allAccounts)
        : allAccounts;

    // Filter out accounts already used in other mappings
    const usedIds = props.params.usedAccountIds;
    if (usedIds && usedIds.size > 0) {
      filteredAccounts = filteredAccounts.filter((account) => {
        // Always include the currently selected account for this row
        if (account.record_id === selectedAccountId.value) {
          return true;
        }
        return !usedIds.has(account.record_id);
      });
    }

    accounts.value = filteredAccounts;
    // Map filtered accounts to options
    const searchOptions = filteredAccounts
      .filter((account) => account.record_name && account.record_id)
      .map((account) => ({
        value: account.record_id,
        label: account.record_name || '',
        record_name: account.record_name || '',
        record_id: account.record_id,
      }));

    // If we have a selected account, ensure it's included in the options
    if (selectedAccountId.value && selectedAccountName.value) {
      const hasSelectedOption = searchOptions.some(
        (opt) => opt.value === selectedAccountId.value,
      );
      if (!hasSelectedOption) {
        searchOptions.unshift({
          value: selectedAccountId.value,
          label: selectedAccountName.value,
          record_name: selectedAccountName.value,
          record_id: selectedAccountId.value,
        });
      }
    }

    if (query === searchTerm.value) {
      accountOptions.value = searchOptions.filter(
        (opt) => opt.record_id !== '__loading__',
      );
    }
  } catch (err) {
    captureException(err);
    const errorMessage =
      (err as { message?: string })?.message ||
      'Failed to search accounts. Please try again.';
    flashesStore.addErrorFlash({
      message: errorMessage,
    });
    ensureDefaultOption();
  } finally {
    isLoading.value = false;
  }
}

function handleSearch(query: string | null) {
  // Handle null/undefined queries
  const searchQuery = query || '';
  searchTerm.value = searchQuery;

  // Mark that user has started searching
  if (searchQuery) {
    hasTriggeredInitialSearch.value = true;
  }

  // Trigger search only when user types at least 3 characters
  if (searchQuery && searchQuery.length >= 3) {
    searchAccounts(searchQuery);
    return;
  }

  // When query is too short, show only the default option if it exists
  // This ensures the input displays the label, not the ID
  if (!searchQuery || searchQuery.length < 3) {
    if (selectedAccountId.value && selectedAccountName.value) {
      const defaultOption = {
        value: selectedAccountId.value,
        label: selectedAccountName.value,
        record_name: selectedAccountName.value,
        record_id: selectedAccountId.value,
      };
      // Check if it's already in options
      const exists = accountOptions.value.some(
        (opt) => opt.value === selectedAccountId.value,
      );
      if (!exists) {
        accountOptions.value = [defaultOption];
      } else {
        // Keep only the default option when query is cleared
        accountOptions.value = accountOptions.value.filter(
          (opt) => opt.value === selectedAccountId.value,
        );
      }
    } else if (!searchQuery) {
      // Only clear options if query is empty and no selected account
      accountOptions.value = [];
    }
  }
}

function updateLocalMapping(
  masterId: string | null,
  masterName: string | null,
) {
  const partnerId = props.params.data?.partner_org_id ?? null;
  if (!partnerId) {
    flashesStore.addErrorFlash({
      message: 'Partner ID is missing.',
    });
    return;
  }

  // Update AG Grid node data immediately so the cell reflects the change
  if (props.params.node) {
    props.params.node.updateData({
      ...props.params.data,
      master_id: masterId,
      master_name: masterName,
    });
    props.params.api.redrawRows({ rowNodes: [props.params.node] });
  }

  props.params.onMappingChanged?.(String(partnerId), masterId, masterName);
}

function handleSelection(value: string | null) {
  if (!value) {
    handleClear();
    return;
  }

  const selectedOption = accountOptions.value.find(
    (opt) => opt.value === value,
  );
  updateLocalMapping(value, selectedOption?.record_name ?? null);
}

function handleClear() {
  updateLocalMapping(null, null);
}

/**
 * Ensures the default option (selected account) is in accountOptions
 * This ensures BittsSelect displays the label (master_name) instead of the ID
 */
function ensureDefaultOption() {
  if (selectedAccountId.value && selectedAccountName.value) {
    const existingOption = accountOptions.value.find(
      (opt) => opt.value === selectedAccountId.value,
    );
    if (!existingOption) {
      // Add the default option at the beginning of the array
      // This ensures it's available for BittsSelect to display the label
      accountOptions.value.unshift({
        value: selectedAccountId.value,
        label: selectedAccountName.value,
        record_name: selectedAccountName.value,
        record_id: selectedAccountId.value,
      });
      return;
    }

    // Update the label if it has changed
    if (existingOption.label !== selectedAccountName.value) {
      existingOption.label = selectedAccountName.value;
      existingOption.record_name = selectedAccountName.value;
    }
  }
}

// Watch for changes to selectedAccountId to ensure default option is always present
watch(
  [selectedAccountId, selectedAccountName],
  () => {
    if (selectedAccountId.value && selectedAccountName.value) {
      ensureDefaultOption();
    } else if (!selectedAccountId.value) {
      // Clear options if selection is cleared
      accountOptions.value = [];
    }
  },
  { immediate: true },
);

// Load selected account when component mounts (for renderer mode)
onMounted(() => {
  // If there's a selected account, ensure it's in the options
  if (selectedAccountId.value && selectedAccountName.value) {
    ensureDefaultOption();
  } else {
    // No selected account, ensure options are empty
    accountOptions.value = [];
  }

  // Add focus listener to trigger search with partner_name when input is focused
  nextTick(() => {
    if (selectRef.value) {
      const selectElement = selectRef.value.$el || selectRef.value;
      const searchInput = selectElement?.querySelector('input');
      if (searchInput) {
        searchInput.addEventListener(
          'focus',
          () => {
            if (
              !hasTriggeredInitialSearch.value &&
              accountOptions.value.length === 0 &&
              partnerName.value &&
              partnerName.value.length >= 3
            ) {
              hasTriggeredInitialSearch.value = true;
              searchAccounts(partnerName.value);
            }
          },
          { once: true },
        );
      }
    }
  });
});
</script>

<style lang="pcss" scoped>
@reference "../../../../../css/theme.css";
</style>

<style lang="pcss">
@reference "../../../../../css/theme.css";

/* Hide the ID div in the selected value */
.c-partner-mapping-tab__account-select
  .ant-select-selection-item
  .c-partner-mapping-tab__account-id,
.c-partner-mapping-tab__account-select
  .ant-select-selection-item
  > div
  > div:last-child.c-partner-mapping-tab__account-id {
  @apply !hidden;
}
</style>
